import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { User } from '../models/user';
import { QueryService } from './query.service';
import { RememberStateService } from './remember-state.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private http = inject(HttpClient);
    private router = inject(Router);
    private rememberStateService = inject(RememberStateService);
    private queryService = inject(QueryService);

    isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    currentUserSubject = new BehaviorSubject<User>(null);

    async login(username: string, password: string): Promise<boolean> {
        const data = { username, password };

        try {
            await firstValueFrom(
                this.http.post<any>(`${environment.apiUrl}/authentication/login`, data, { withCredentials: true })
            );
            await this.isJwtAuthenticated();
            return this.isAuthenticatedSubject.getValue();
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);
            throw error.status === 400 ? error.error.validationErrors[0].errorMessage : 'Unknown error';
        }
    }

    async logout(addReturnUrl = true) {
        const isAuthenticated = this.isAuthenticatedSubject.getValue();
        if (isAuthenticated) {
            await firstValueFrom(
                this.http.post<any>(`${environment.apiUrl}/authentication/Logout`, {}, { withCredentials: true })
            );
            this.queryService.queryClient.clear();
        }
        this.rememberStateService.clear(['languageId', 'darkMode']);

        this.updateIsAuthenticatedSubject(false);
        this.updateCurrentUser(null);

        const routerOptions = addReturnUrl && isAuthenticated ? { queryParams: { returnUrl: this.router.url } } : {};
        return this.router.navigate(['/login'], routerOptions);
    }

    async getCurrentUser(): Promise<User> {
        try {
            const data = await firstValueFrom(
                this.http.get(`${environment.apiUrl}/query/CurrentUser`, { withCredentials: true })
            );
            const user = data ? new User(data) : null;

            this.updateIsAuthenticatedSubject(!!data);
            this.updateCurrentUser(user);

            return user;
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);

            throw error;
        }
    }

    refreshAccessToken() {
        return this.http.post<any>(
            `${environment.apiUrl}/authentication/RefreshAccessToken`,
            {},
            { withCredentials: true }
        );
    }

    private updateIsAuthenticatedSubject(value) {
        if (this.isAuthenticatedSubject.getValue() !== value) {
            this.isAuthenticatedSubject.next(value);
        }
    }

    private updateCurrentUser(user: User) {
        if (this.currentUserSubject.getValue() !== user) {
            this.currentUserSubject.next(user);
        }
    }

    async isJwtAuthenticated(): Promise<void> {
        try {
            const result = await firstValueFrom(
                this.http.post<any>(`${environment.apiUrl}/authentication/IsAuth`, {}, { withCredentials: true })
            );
            this.updateIsAuthenticatedSubject(result);
        } catch (_err) {
            this.updateIsAuthenticatedSubject(false);
        }
    }

    async forgetPassword(username: string): Promise<void> {
        const data = { username };

        try {
            /*  return await firstValueFrom(
                this.http.post<any>(`${environment.apiUrl}/command/ForgotPassword`, data, { withCredentials: true }) 

            ); TODO- real backend infrastructure*/
            await new Promise((resolve) => setTimeout(resolve, 500));

            // return a fake success result for testing
            return {
                success: true,
                message: 'Fake password reset email sent.'
            } as any;
        } catch (error) {
            throw error.status === 400 ? error.error.validationErrors[0].errorMessage : 'Unknown error';
        }
    }
}

export type AuthTokens = {
    accessToken: string;
    refreshToken: string;
};
